import React, { useEffect, useState } from "react";
import { imageUrl } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen image gallery with zoom + arrow navigation.
 * paths: string[]   storage paths to view via /api/files/{path}
 * startIndex: number
 */
const Lightbox = ({ paths = [], startIndex = 0, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setIdx(startIndex);
    setZoom(1);
  }, [startIndex, paths]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!paths?.length) return null;

  const prev = () => {
    setZoom(1);
    setIdx((i) => (i - 1 + paths.length) % paths.length);
  };
  const next = () => {
    setZoom(1);
    setIdx((i) => (i + 1) % paths.length);
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[60] flex flex-col"
      data-testid="lightbox-overlay"
    >
      <div className="flex items-center justify-between p-4">
        <div className="label-eyebrow text-white/70">
          {idx + 1} / {paths.length}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="lightbox-zoom-out"
            onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
            className="nb-btn bg-white p-2"
          >
            <ZoomOut size={18} strokeWidth={2.5} />
          </button>
          <button
            data-testid="lightbox-zoom-in"
            onClick={() => setZoom((z) => Math.min(4, z + 0.5))}
            className="nb-btn bg-white p-2"
          >
            <ZoomIn size={18} strokeWidth={2.5} />
          </button>
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            className="nb-btn nb-btn-primary p-2"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        {paths.length > 1 && (
          <button
            data-testid="lightbox-prev"
            onClick={prev}
            className="absolute left-4 nb-btn bg-white p-3 z-10"
          >
            <ChevronLeft size={26} strokeWidth={2.5} />
          </button>
        )}
        <img
          src={imageUrl(paths[idx])}
          alt={`Photo ${idx + 1}`}
          className="max-h-[80vh] max-w-[90vw] object-contain transition-transform duration-150"
          style={{ transform: `scale(${zoom})` }}
          onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
          data-testid="lightbox-image"
        />
        {paths.length > 1 && (
          <button
            data-testid="lightbox-next"
            onClick={next}
            className="absolute right-4 nb-btn bg-white p-3 z-10"
          >
            <ChevronRight size={26} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {paths.length > 1 && (
        <div className="p-4 flex justify-center gap-2 overflow-x-auto scrollbar-clean">
          {paths.map((p, i) => (
            <button
              key={p}
              onClick={() => {
                setZoom(1);
                setIdx(i);
              }}
              className="shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2"
              style={{ borderColor: i === idx ? "#228EBF" : "#333" }}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={imageUrl(p)} className="w-full h-full object-cover" alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Lightbox;
